import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, Download, CheckCircle2 } from "lucide-react";
import { api, errMsg, download, today, fmtDT } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, DataTable, Badge, Field, Select } from "@/components/common";

const STEPS = ["Upload", "Map columns", "Validate & options", "Results"];

function Steps({ step }) {
  return (
    <div className="mb-5 flex flex-wrap gap-2" data-testid="import-steps">
      {STEPS.map((s, i) => (
        <span key={s} className={`rounded-full px-3 py-1 text-xs font-semibold ${i === step ? "bg-[#283F91] text-white" : i < step ? "bg-blue-50 text-[#283F91]" : "bg-[var(--surface)] text-[var(--muted-ink)]"}`}>{i + 1}. {s}</span>
      ))}
    </div>
  );
}

export default function ImportExport() {
  const { meta, can } = useApp();
  const [step, setStep] = useState(0);
  const [parsed, setParsed] = useState(null);
  const [mapping, setMapping] = useState({});
  const [val, setVal] = useState(null);
  const [opts, setOpts] = useState({ mode: "new_only", import_history: true, assignment_rule: "single", employee_ids: [], confirm_update: false, resolutions: {} });
  const [result, setResult] = useState(null);
  const [hist, setHist] = useState([]);
  const [busy, setBusy] = useState(false);
  const loadHist = () => api.get("/import/history").then((r) => setHist(r.data)).catch(() => {});
  useEffect(() => { loadHist(); }, []);
  const staff = (meta?.staff || []).filter((s) => s.active !== false);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/import/parse", fd);
      setParsed(data);
      setMapping(data.suggested_mapping);
      setStep(1);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const validate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/import/validate", { staging_id: parsed.staging_id, mapping });
      setVal(data);
      setStep(2);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const commit = async () => {
    setBusy(true);
    try {
      const o = { ...opts, employee_ids: opts.assignment_rule === "single" ? opts.employee_ids.slice(0, 1) : opts.employee_ids };
      const { data } = await api.post("/import/commit", { staging_id: parsed.staging_id, mapping, options: o });
      setResult(data);
      setStep(3);
      loadHist();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const reset = () => { setStep(0); setParsed(null); setVal(null); setResult(null); setOpts({ ...opts, resolutions: {}, confirm_update: false }); };
  const fields = parsed?.target_fields || [];

  return (
    <div>
      <PageHeader title="Import / Export" subtitle="Import patients from Excel, CSV or the legacy PDF tracking sheet. Use synthetic data only until HIPAA safeguards and BAAs are in place." />
      {can("action:import") && (
        <section className="panel mb-6 p-5" data-testid="import-wizard">
          <Steps step={step} />
          {step === 0 && (
            <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-10 text-center transition-colors hover:border-[#283F91]">
              <Upload className="text-[#283F91]" />
              <span className="text-sm font-semibold">{busy ? "Reading file…" : "Choose a file (.xlsx, .csv, .pdf)"}</span>
              <span className="text-xs text-[var(--muted-ink)]">Columns from the original tracking sheet (ID #, DOB, Last Labs, Next Lab, STATUS, Insurance, Pharmacy, Rx 1–4 with refills, Adherence) are recognized automatically.</span>
              <input type="file" className="hidden" accept=".xlsx,.xls,.csv,.pdf" onChange={(e) => upload(e.target.files[0])} data-testid="import-file-input" />
            </label>
          )}
          {step === 1 && parsed && (
            <div>
              <p className="mb-3 text-sm"><b>{parsed.file_name}</b> · {parsed.row_count} rows detected · {parsed.headers.length} columns</p>
              {parsed.requires_review && <p className="mb-3 rounded bg-amber-50 p-2 text-xs text-amber-800" data-testid="pdf-review-note">PDF text was extracted by column position. Review the mapping and preview carefully — rows with unclear values will be flagged in validation.</p>}
              {parsed.warnings.map((w) => <p key={w} className="mb-2 text-xs text-red-600">{w}</p>)}
              <div className="mb-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-4" data-testid="mapping-grid">
                {parsed.headers.map((h) => (
                  <Field key={h} label={h}>
                    <select className="inp" value={mapping[h] || ""} onChange={(e) => setMapping({ ...mapping, [h]: e.target.value })} data-testid={`map-${h.replace(/[^a-zA-Z0-9]/g, "_")}`}>
                      <option value="">— Ignore —</option>
                      {fields.map((f) => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </Field>
                ))}
              </div>
              <div className="mb-4 max-h-72 overflow-auto rounded border">
                <table className="tbl w-full" data-testid="import-preview-table">
                  <thead><tr>{parsed.headers.map((h) => <th key={h}>{h}</th>)}</tr></thead>
                  <tbody>{parsed.preview.map((r, i) => <tr key={i}>{parsed.headers.map((h) => <td key={h}>{r[h] || ""}</td>)}</tr>)}</tbody>
                </table>
              </div>
              <div className="flex gap-2"><button className="btn btn-ghost" onClick={reset}>Start over</button>
                <button className="btn btn-primary" disabled={busy} onClick={validate} data-testid="import-validate-btn">Validate</button></div>
            </div>
          )}
          {step === 2 && val && (
            <div>
              <div className="mb-4 flex flex-wrap gap-2" data-testid="validation-counts">
                {Object.entries(val.counts).map(([k, v]) => <span key={k} className="rounded-md bg-[var(--surface)] px-3 py-1.5 text-xs"><b>{v}</b> {k.replace("_", " ")}</span>)}
              </div>
              {val.history && (
                <div className="mb-4 flex flex-wrap gap-2" data-testid="history-counts">
                  {[["new", "Patients to be imported", val.counts.new || 0], ["prescriptions", "Prescriptions identified"], ["refills", "Refill dates identified"], ["fills_new", "Fill records to be created"], ["fills_duplicate", "Duplicate fills skipped"], ["review", "Invalid dates requiring review"]].map(([k, l, n]) => (
                    <span key={k} className={`rounded-md px-3 py-1.5 text-xs ${k === "review" && val.history.review ? "bg-amber-50 text-amber-900" : "bg-[var(--surface)]"}`} data-testid={`history-count-${k}`}><b>{n ?? val.history[k]}</b> {l}</span>
                  ))}
                </div>
              )}
              <div className="mb-4 grid gap-4 lg:grid-cols-3">
                <Field label="Import mode">
                  <Select value={opts.mode} onChange={(v) => setOpts({ ...opts, mode: v })} testid="import-mode-select"
                    options={[{ value: "new_only", label: "Import new patients only (skip duplicates)" }, { value: "update", label: "Update existing patients" }, { value: "review", label: "Review conflicts manually" }]} />
                </Field>
                <Field label="Assignment rule">
                  <Select value={opts.assignment_rule} onChange={(v) => setOpts({ ...opts, assignment_rule: v })} testid="import-rule-select"
                    options={[{ value: "single", label: "Assign all to one employee" }, { value: "round_robin", label: "Distribute evenly (round robin)" }, { value: "by_column", label: "Use employee email column, fallback below" }]} />
                </Field>
                <label className="mt-6 flex items-center gap-2 text-sm"><input type="checkbox" checked={opts.import_history} onChange={(e) => setOpts({ ...opts, import_history: e.target.checked })} data-testid="import-history-checkbox" />Import prescription &amp; refill history (refill dates become fill records)</label>
              </div>
              <div className="mb-4">
                <span className="lbl">Assigned employee{opts.assignment_rule !== "single" ? "s" : ""}</span>
                <div className="mt-1.5 flex flex-wrap gap-2" data-testid="import-employee-options">
                  {staff.map((s) => (
                    <label key={s.id} className="flex items-center gap-1.5 rounded border px-2 py-1 text-xs">
                      <input type={opts.assignment_rule === "single" ? "radio" : "checkbox"} name="imp-emp" checked={opts.employee_ids.includes(s.id)} data-testid={`import-emp-${s.id}`}
                        onChange={(e) => setOpts({ ...opts, employee_ids: opts.assignment_rule === "single" ? [s.id] : e.target.checked ? [...opts.employee_ids, s.id] : opts.employee_ids.filter((x) => x !== s.id) })} />{s.name}
                    </label>
                  ))}
                </div>
              </div>
              {opts.mode === "update" && <label className="mb-4 flex items-center gap-2 rounded bg-amber-50 p-2 text-sm text-amber-900"><input type="checkbox" checked={opts.confirm_update} onChange={(e) => setOpts({ ...opts, confirm_update: e.target.checked })} data-testid="import-confirm-update" />I confirm existing patient records may be updated. Previous values will be preserved in the record history.</label>}
              <DataTable testid="validation-table" rowKey="index" pageSize={20} rows={val.results}
                columns={[{ key: "index", label: "Row", render: (r) => r.index + 1 }, { key: "page", label: "Page" }, { key: "patient_id", label: "Patient ID" },
                  { key: "status", label: "Result", render: (r) => <Badge value={{ new: "Current", duplicate: "Pending", conflict: "Needs Follow-up", invalid: "Overdue", file_duplicate: "Overdue" }[r.status] ? r.status : r.status} /> },
                  { key: "history", label: "Rx / Refills / New fills", sortable: false, render: (r) => <span className="text-xs" data-testid={`row-history-${r.index}`}>{r.history.prescriptions} / {r.history.refills} / {r.history.fills_new}</span> },
                  { key: "errors", label: "Errors", text: (r) => r.errors.join("; "), render: (r) => <span className="text-xs text-red-600">{r.errors.join("; ")}</span> },
                  { key: "warnings", label: "Warnings", text: (r) => r.warnings.join("; "), render: (r) => <span className="text-xs text-amber-700">{r.warnings.join("; ")}</span> },
                  { key: "diffs", label: "Conflicts", sortable: false, render: (r) => <span className="text-xs">{Object.entries(r.diffs).map(([k, v]) => `${k}: ${v.current} → ${v.incoming}`).join("; ")}</span> },
                  { key: "_res", label: "Decision", sortable: false, render: (r) => (opts.mode === "review" && ["duplicate", "conflict"].includes(r.status) ? (
                    <select className="inp h-8 w-28 text-xs" value={opts.resolutions[r.index] || "skip"} data-testid={`resolve-${r.index}`} onChange={(e) => setOpts({ ...opts, resolutions: { ...opts.resolutions, [r.index]: e.target.value } })}>
                      <option value="skip">Skip</option><option value="update">Update</option></select>) : r.status === "new" ? "Import" : ["invalid", "file_duplicate"].includes(r.status) ? "Rejected" : opts.mode === "update" ? "Update" : "Skip") }]} />
              <div className="mt-4 flex gap-2"><button className="btn btn-ghost" onClick={() => setStep(1)}>Back to mapping</button>
                <button className="btn btn-primary" disabled={busy || !opts.employee_ids.length} onClick={commit} data-testid="import-commit-btn">Confirm import</button></div>
            </div>
          )}
          {step === 3 && result && (
            <div data-testid="import-results">
              <p className="mb-3 flex items-center gap-2 font-semibold text-emerald-700"><CheckCircle2 size={18} />Import complete — {result.file_name}</p>
              <div className="mb-3 flex flex-wrap gap-2">{Object.entries(result.counts).map(([k, v]) => <span key={k} className="rounded-md bg-[var(--surface)] px-3 py-1.5 text-xs" data-testid={`result-${k}`}><b>{v}</b> {k.replaceAll("_", " ")}</span>)}</div>
              {result.errors.length > 0 && <ul className="mb-3 max-h-40 overflow-auto text-xs text-red-600">{result.errors.map((e) => <li key={e.row}>Row {e.row} ({e.patient_id || "no ID"}): {e.errors.join("; ")}</li>)}</ul>}
              {result.warnings?.length > 0 && <div className="mb-3" data-testid="import-review-list"><p className="mb-1 text-xs font-semibold text-amber-800">Imported, but needs review ({result.warnings.length})</p><ul className="max-h-40 overflow-auto text-xs text-amber-700">{result.warnings.map((e) => <li key={e.row}>Row {e.row} ({e.patient_id || "no ID"}): {e.warnings.join("; ")}</li>)}</ul></div>}
              <button className="btn btn-primary" onClick={reset} data-testid="import-new-btn">Import another file</button>
            </div>
          )}
        </section>
      )}
      {can("action:export") && (
        <section className="panel mb-6 flex flex-wrap items-center gap-2 p-5" data-testid="export-section">
          <span className="mr-2 text-sm font-semibold">Export patient list</span>
          {["csv", "xlsx", "pdf"].map((f) => <button key={f} className="btn btn-ghost" onClick={() => download(`/export/patients?format=${f}`, { filename: `patients_${today()}.${f}` })} data-testid={`export-patients-${f}`}><Download size={14} />{f.toUpperCase()}</button>)}
          <span className="text-xs text-[var(--muted-ink)]">Exports include only patients you can access and are audit-logged.</span>
        </section>
      )}
      <h2 className="mb-3 text-base font-semibold md:text-lg">Import history</h2>
      <DataTable testid="import-history-table" rows={hist} searchable={false}
        columns={[{ key: "at", label: "Date", render: (r) => fmtDT(r.at) }, { key: "admin_name", label: "Administrator" }, { key: "file_name", label: "File" },
          { key: "mode", label: "Mode" }, { key: "imported", label: "Imported", text: (r) => r.counts.imported, render: (r) => r.counts.imported },
          { key: "updated", label: "Updated", render: (r) => r.counts.updated }, { key: "skipped", label: "Skipped", render: (r) => r.counts.skipped },
          { key: "errors", label: "Errors", render: (r) => r.counts.errors }, { key: "rx", label: "Rx / Fills", render: (r) => `${r.counts.prescriptions} / ${r.counts.fills}` }]} />
    </div>
  );
}
